import {
	OrderProduct,
	OrderQuotaErrorDto,
	ProductSize,
	SapCodeQuotaAvailabilityDto,
} from '../api-client';

/**
 * Reads the sap code of a size.
 * <p>
 * This needs a helper rather than direct field access because the generated type declares both sapCode and
 * idSapCode, while the Web model exposes only one of them: ProductSizeDto.IdSapCode is mapped onto SapCode.
 * Reading the wrong field does not fail compilation, since both are optional, it just silently yields a quota
 * that is never found.
 */
export const sapCodeOfSize = (
	size?: Pick<ProductSize, 'sapCode' | 'idSapCode'>,
): string | undefined => size?.sapCode ?? size?.idSapCode ?? undefined;

/** A quota is exhausted when nothing is left. This also covers a quota configured to zero. */
export const isQuotaExhausted = (quota?: SapCodeQuotaAvailabilityDto): boolean =>
	!!quota && quota.remainingQuantity <= 0;

/** Remaining over maximum, e.g. "Available 12 of 50". */
export const quotaAvailabilityLabel = (
	quota?: SapCodeQuotaAvailabilityDto,
): string | undefined =>
	quota
		? `Available ${quota.remainingQuantity} of ${quota.maxQuantity}`
		: undefined;

/** Exhausted label, telling apart a quota configured to zero from a remaining quantity used up. */
export const quotaExhaustedLabel = (
	quota?: SapCodeQuotaAvailabilityDto,
): string | undefined => {
	if (!quota) return undefined;

	return quota.maxQuantity === 0
		? 'Not orderable, quota set to zero'
		: `Quota exhausted, ${quota.usedQuantity} of ${quota.maxQuantity} already ordered`;
};

/**
 * Also reports the quantity committed in other open carts of the area, e.g.
 * "30 confirmed plus 15 in other carts out of 50". Returns undefined when nothing sits in other drafts, to
 * avoid adding noise.
 */
export const quotaPendingInOtherDraftsLabel = (
	quota?: Pick<
		SapCodeQuotaAvailabilityDto,
		'usedQuantity' | 'maxQuantity' | 'pendingInOtherDraftsQuantity'
	>,
): string | undefined =>
	quota && quota.pendingInOtherDraftsQuantity > 0
		? `${quota.usedQuantity} confirmed plus ${quota.pendingInOtherDraftsQuantity} in other carts out of ${quota.maxQuantity}`
		: undefined;

export interface QuotaViolation {
	sapCode: string;
	requestedQuantity: number;
	maxQuantity: number;
	remainingQuantity: number;
	usedQuantity: number;
	/** Quantity sitting in other open carts of the area. It does not consume quota, it only explains the number. */
	pendingInOtherDraftsQuantity: number;
}

/**
 * Sums the cart quantities per sap code.
 * <p>
 * A quota is a pool per sap code, not per row: the same code can show up on several rows, even across
 * different facilities, and has to be compared with the remaining quantity once summed.
 */
export const aggregateCartQuantitiesBySapCode = (
	products?: OrderProduct[],
): Record<string, number> => {
	const totals: Record<string, number> = {};

	(products ?? []).forEach((product) => {
		(product.sizes ?? []).forEach((size) => {
			const sapCode = sapCodeOfSize(size);
			if (!sapCode) return;

			totals[sapCode] = (totals[sapCode] ?? 0) + (size.quantity ?? 0);
		});
	});

	return totals;
};

/**
 * Cart rows going over the available quota, one per sap code.
 * Codes with no quota configured do not show up: they stay unlimited.
 */
export const findQuotaViolations = (
	products?: OrderProduct[],
	availabilityBySapCode?: Record<string, SapCodeQuotaAvailabilityDto>,
): QuotaViolation[] => {
	if (!availabilityBySapCode) return [];

	const requested = aggregateCartQuantitiesBySapCode(products);

	return Object.keys(requested)
		.map((sapCode) => ({ sapCode, quota: availabilityBySapCode[sapCode] }))
		.filter(
			(entry) =>
				!!entry.quota && requested[entry.sapCode] > entry.quota.remainingQuantity,
		)
		.map((entry) => ({
			sapCode: entry.sapCode,
			requestedQuantity: requested[entry.sapCode],
			maxQuantity: entry.quota!.maxQuantity,
			remainingQuantity: entry.quota!.remainingQuantity,
			usedQuantity: entry.quota!.usedQuantity,
			pendingInOtherDraftsQuantity: entry.quota!.pendingInOtherDraftsQuantity,
		}));
};

/** The violation for a given sap code, used to highlight the cart row. */
export const violationForSapCode = (
	violations: QuotaViolation[],
	sapCode?: string,
): QuotaViolation | undefined =>
	sapCode ? violations.find((entry) => entry.sapCode === sapCode) : undefined;

/** Banner text at the top of the cart. Returns undefined when there is nothing to report. */
export const quotaViolationsBannerLabel = (
	violations: QuotaViolation[],
): string | undefined => {
	if (violations.length === 0) return undefined;

	return violations.length === 1
		? 'One row exceeds the available quota and must be fixed before proceeding.'
		: `${violations.length} rows exceed the available quota and must be fixed before proceeding.`;
};

/** Row level detail of the violation, e.g. "Requested 15, available 10 of 50". */
export const quotaViolationLabel = (
	violation?: QuotaViolation,
): string | undefined =>
	violation
		? `Requested ${violation.requestedQuantity}, available ${violation.remainingQuantity} of ${violation.maxQuantity}`
		: undefined;

/**
 * Summary of the errors returned by the backend with a 409 on order confirmation, one per sap code.
 */
export const quotaErrorsSummary = (errors?: OrderQuotaErrorDto[]): string =>
	(errors ?? [])
		.map(
			(error) =>
				`${error.sapCode}: requested ${error.requestedQuantity}, available ${error.remainingQuantity} of ${error.maxQuantity}`,
		)
		.join('; ');
